import groupBy from 'lodash.groupby';

import {
  BasketViewModel,
  HotelAvailabilityStatusType,
  PackageDetailRoomViewModel,
  PackagePrebookAddTransferRequestViewModel,
  PackageTransferViewModel,
  RoomVariationViewModel,
} from '@api/v3';
import { cloudineryImage } from '@components/views/hotel/detail/utils/features';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';

import { GalleryCategory, HotelRooms } from './types';

export const groupImagesByCategory = <
  T extends { url?: string; hotelImageCategoryName?: string }
>(
  images: T[],
  groupingKey?: string,
  hotelName?: string,
  rooms?: PackageDetailRoomViewModel[]
) => {
  const roomsCategory = getRoomsGalleryCategory(rooms ?? []);

  const imagesForGallery = images?.map((p, i) => ({
    ...p,
    original:
      cloudineryImage(p.url, '840', null) ?? '/static/images/setur-static.jpg',
    originalAlt: `${hotelName || ''} ${p?.hotelImageCategoryName || ''} ${
      i + 1
    }`,
    thumbnailAlt: `${hotelName || ''} ${p.hotelImageCategoryName || ''} küçük ${
      i + 1
    }`,
    thumbnail:
      cloudineryImage(p.url, '137', null) ?? '/static/images/setur-static.jpg',
  }));
  const imageCategories = groupingKey
    ? groupBy(imagesForGallery, groupingKey)
    : { 'Oda Fotografları': imagesForGallery };
  return {
    ...imageCategories,
    all: [...imagesForGallery, ...roomsCategory],
  } as { [key: string]: typeof imagesForGallery };
};

export const getRoomsGalleryCategory = (
  rooms: PackageDetailRoomViewModel[]
) => {
  const roomsCategory: GalleryCategory[] = [];

  rooms?.forEach((room) =>
    room.images.forEach((roomImages) =>
      roomsCategory.push({
        url: roomImages?.url ?? '/static/images/setur-static.jpg',
        hotelImageCategoryName: 'Odalar',
        original:
          cloudineryImage(roomImages?.url, '420', '250') ??
          '/static/images/setur-static.jpg',
        originalAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
        thumbnailAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
        thumbnail:
          cloudineryImage(roomImages?.url, '137', '65') ??
          '/static/images/setur-static.jpg',
        description:
          room.name && room.typeName ? room.name + ' ' + room.typeName : '',
      })
    )
  );

  return roomsCategory;
};

export const getLowestVariation = (roomsDetail: HotelRooms) => {
  const roomVariations: RoomVariationViewModel[] = [];

  if (!roomsDetail) return;

  roomsDetail.informations?.forEach((room) =>
    roomVariations.push(...room.variations)
  );

  const lowestPrice =
    Math.min(
      ...roomVariations.map((item) => item.totalDiscountedSellingPrice)
    ) ?? 0;

  if (!lowestPrice) return roomVariations?.[0];

  const lowestPriceItem = roomVariations?.find(
    (item) => item.totalDiscountedSellingPrice === lowestPrice
  );
  return lowestPriceItem;
};

export const getAvailableRoom = (roomsDetail: HotelRooms): boolean => {
  if (!roomsDetail) return false;

  const availableRoom = roomsDetail?.informations.find(
    (roomInfo) =>
      roomInfo.availabilityStatus === HotelAvailabilityStatusType.AVAILABLE
  );

  if (availableRoom) return true;
  return false;
};

export const getGuestInformationFromRooms = (
  rooms: ListingSearchRoomInterface[]
) => {
  const adultCount = rooms
    ?.map((item) => item.adultCount)
    .reduce((acc, val) => acc + val);

  const childCount = rooms
    ?.map((item) => item.childrenList?.length || 0)
    .reduce((acc, val) => acc + val);

  return { adultCount: adultCount, childCount };
};

export const prepareTransferForPrebookAdd = (
  adultCount: number,
  childCount: number,
  childBirthdates: Array<string>,
  selectedDepatureTransfer: PackageTransferViewModel,
  selectedReturnTransfer: PackageTransferViewModel
) => {
  const transferItem: PackagePrebookAddTransferRequestViewModel = {
    adultCount: adultCount,
    childCount: childCount,
    childrenBirthDates: childBirthdates,
    departure: {
      productId: selectedDepatureTransfer.productId,
      categoryType: selectedDepatureTransfer.transferCategoryType,
      pickUpLocationId: selectedDepatureTransfer.pickupLocationId,
      dropoffLocationId: selectedDepatureTransfer.dropOffLocationId,
      pickUpLocationName: selectedDepatureTransfer.pickupLocationName,
      dropOffLocationName: selectedDepatureTransfer.dropOffLocationName,
      transferDate: selectedDepatureTransfer.date,
      originalSalePrice: selectedDepatureTransfer.originalSalePrice,
      trySalePrice: selectedDepatureTransfer.trySalePrice,
      currency: selectedDepatureTransfer.currency,
      name: selectedDepatureTransfer.name,
    },
    return: {
      productId: selectedReturnTransfer.productId,
      categoryType: selectedReturnTransfer.transferCategoryType,
      pickUpLocationId: selectedReturnTransfer.pickupLocationId,
      dropoffLocationId: selectedReturnTransfer.dropOffLocationId,
      pickUpLocationName: selectedReturnTransfer.pickupLocationName,
      dropOffLocationName: selectedReturnTransfer.dropOffLocationName,
      transferDate: selectedReturnTransfer.date,
      originalSalePrice: selectedReturnTransfer.originalSalePrice,
      trySalePrice: selectedReturnTransfer.trySalePrice,
      currency: selectedReturnTransfer.currency,
      name: selectedReturnTransfer.name,
    },
  };
  return transferItem;
};

export function extractItems(data: BasketViewModel) {
  const items: any[] = [];

  if (data.flights && data.flights.length > 0) {
    items.push(...data.flights);
  }

  if (data.hotels && data.hotels.length > 0) {
    items.push(...data.hotels);
  }

  if (data.tours && data.tours.length > 0) {
    items.push(...data.tours);
  }

  if (data.transfers && data.transfers.length > 0) {
    items.push(...data.transfers);
  }

  if (data.extraServices && data.extraServices.length > 0) {
    items.push(...data.extraServices);
  }

  return items;
}
